import { useCallback } from 'react';
import useOpenCommsParams from '~/client/features/Comms/hooks/useOpenCommsParams';
import getCommsConfig from '~/client/features/Comms/utils/getCommsConfig';
import useUserPreferenceId from '~/client/hooks/cookies/useUserPreferenceId';
import useRouter from '~/client/hooks/router/useRouter';
import useIsLoggedIn from '~/client/hooks/user/useIsLoggedIn';
import useSite from '~/client/hooks/useSite';
import useAppContext from '~/shared/context/AppContext/useAppContext';
import LANGUAGE_COOKIE from '~/shared/cookies/LANGUAGE_COOKIE';
import { useChangeCurrentLanguageMutation } from '~/shared/graphql/changeCurrentLanguage.generated';
import { useSportsbetUpdateLanguageMutation } from '~/shared/graphql/sportsbet/SportsbetUpdateLanguageMutation.generated';
import sendEventToUmami from '~/shared/trackEvent/sendEventToUmami';
import Cookie from '~/shared/utils/Cookie';
import inAYear from '~/shared/utils/date/inYear';
import nativeRedirect from '~/shared/utils/router/nativeRedirect';
import sendLanguageToGtm from './helpers/sendLanguageToGtm';
import useLanguageFeatureFlags from './useLanguageFeatureFlags';
import CommsCommands from '@/client/helpers/CommsCommands';

const useLanguageSwitcher = (isProtoEnabled?: boolean) => {
  const [changeLanguageMutation] = useChangeCurrentLanguageMutation();
  const [changeSportsbetLanguageMutation] =
    useSportsbetUpdateLanguageMutation();
  const router = useRouter();
  const isLoggedIn = useIsLoggedIn();
  const { languages } = useAppContext();
  const { isBridgeSite, site, siteConfig } = useSite();
  const commsParams = useOpenCommsParams();
  const languageFlags = useLanguageFeatureFlags();
  const enabledLanguages = languages.filter(
    (lang) => languageFlags?.[lang] !== false,
  );
  const [userPreferenceId] = useUserPreferenceId();

  const updateLang = async (value: string | Language) => {
    await changeLanguageMutation({
      variables: { language: value as string },
      optimisticResponse: {
        userManagementChangeLanguage: {
          success: true,
          __typename: 'UserManagementChangeLanguagePayload',
          userManagement: {
            __typename: 'UserManagement',
            currentProfile: {
              language: value as string,
              __typename: 'UserManagementProfile',
            },
          },
        },
      },
    });
    if (isBridgeSite) {
      const id = userPreferenceId;
      const language = value as string;
      await changeSportsbetLanguageMutation({
        variables: { id, site, language },
        optimisticResponse: {
          sportsbetPreferencesUpdateLanguage: {
            __typename: 'SportsbetPreferencesUpdateLanguagePayload',
            userPreferences: {
              id,
              language,
              __typename: 'SportsbetPreferencesUserPreference',
            },
          },
        },
      });
    }
  };

  const inAYearMemo = useCallback(() => inAYear(), []);

  const handleChange = async (value: string) => {
    const code =
      enabledLanguages.find((lang) => lang === value) ?? siteConfig.defaultLang;

    sendEventToUmami(
      {
        languageCode: code,
      },
      'changeLanguage',
    );

    sendLanguageToGtm({
      info: `previous-${router.locale}_new-${code}`,
      category: 'language_list_item',
    });

    if (isLoggedIn) {
      await updateLang(code);
    }

    if (!isProtoEnabled) {
      CommsCommands.update({
        args: { ...getCommsConfig(commsParams), language: code },
      });
    }

    Cookie.set(LANGUAGE_COOKIE, code, { expires: inAYearMemo() });
    nativeRedirect(router.asPath, code, router.defaultLocale);
  };

  return { handleChange, router, languages: enabledLanguages };
};

export default useLanguageSwitcher;
